import {
  CHAR_WIDTH_PX,
  DROPDOWN_PADDING_PX,
  MAX_LABEL_CHARS,
} from "./selectConstants";

const IP_REGEX = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;

/**
 * Rough width a dropdown needs to show its longest option on one line.
 * Very long labels are capped (they wrap instead of widening the panel further)
 * and SelectFieldShell applies the final cap against the viewport.
 *
 * @param {Array<string|number>} labels option labels shown in the list
 * @param {number} extraPaddingPx row indentation, checkboxes, icons, etc.
 */
export const estimateOptionsContentWidth = (labels = [], extraPaddingPx = 0) => {
  let longestLabelLength = 0;

  (Array.isArray(labels) ? labels : []).forEach((label) => {
    const labelLength = String(label ?? "").length;
    if (labelLength > longestLabelLength) longestLabelLength = labelLength;
  });

  const clampedLength = Math.min(longestLabelLength, MAX_LABEL_CHARS);
  return clampedLength * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX + extraPaddingPx;
};

const compareIpLabels = (labelA, labelB) => {
  const matchA = labelA.match(IP_REGEX);
  const matchB = labelB.match(IP_REGEX);

  if (!matchA || !matchB) return null;

  for (let i = 1; i <= 4; i++) {
    const diff = parseInt(matchA[i], 10) - parseInt(matchB[i], 10);
    if (diff !== 0) return diff;
  }

  const prefixA = matchA[5] !== undefined ? parseInt(matchA[5], 10) : 32;
  const prefixB = matchB[5] !== undefined ? parseInt(matchB[5], 10) : 32;
  return prefixA - prefixB;
};

export const compareSelectorLabels = (a, b) => {
  const labelA = String(a ?? "").trim();
  const labelB = String(b ?? "").trim();

  const ipResult = compareIpLabels(labelA, labelB);
  if (ipResult !== null) return ipResult;

  const isANumeric = !isNaN(labelA);
  const isBNumeric = !isNaN(labelB);

  if (isANumeric && isBNumeric) return Number(labelA) - Number(labelB);
  if (!isANumeric && !isBNumeric) return labelA.localeCompare(labelB);
  return isANumeric ? -1 : 1;
};

export const sortSelectorOptions = (options = [], applySorting = true) => {
  const optionList = Array.isArray(options) ? [...options] : [];

  if (!applySorting) return optionList;

  return optionList.sort((a, b) => compareSelectorLabels(a?.label, b?.label));
};

export const buildGroupedSelectorOptions = ({ options = [], groupParam, getParentOption }) => {
  const groups = {};

  options.forEach((option) => {
    const groupName = option[groupParam] || "Other";
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(option);
  });

  return Object.keys(groups)
    .sort(compareSelectorLabels)
    .map((groupName) => ({
      groupName,
      // Parent options are only selectable when the caller opts in, but keeping
      // this payload here makes parent-selectable usages predictable later.
      parentOption:
        typeof getParentOption === "function"
          ? getParentOption(groupName, groups[groupName])
          : {
              value: groupName,
              label: groupName,
              [groupParam]: groupName,
              is_parent_option: true,
            },
      options: sortSelectorOptions(groups[groupName]),
    }));
};
